'use client';

import { formatDateInput } from '@/utils/formatDateInput';
import type { Member } from '../../types';
import { TextInput } from '../shared/TextInput';

interface MemberCardProps {
  member: Member;
  index: number;
  errors: Record<string, string>;
  onUpdate: (field: keyof Member, value: string) => void;
  onRemove?: () => void;
}

/**
 * MemberCard component for the MemberDetailsStep.
 * Displays form fields for a single household member.
 */
export function MemberCard({
  member,
  index,
  errors,
  onUpdate,
  onRemove,
}: MemberCardProps) {
  return (
    <div className='space-y-8 rounded-[32px] bg-[#EBF2EF] p-8'>
      <div className='flex items-center justify-between pl-[20px]'>
        <h3 className='flex-1 text-[18px] leading-[130%] font-bold text-black'>
          New Member{index > 0 ? ` ${index + 1}` : ''}
        </h3>
        {onRemove && (
          <button
            type='button'
            onClick={onRemove}
            className='cursor-pointer text-[18px] leading-[130%] font-normal text-black'
          >
            Remove
          </button>
        )}
      </div>

      {/* First Name */}
      <div>
        <TextInput
          type='text'
          id={`member_${index}_first_name`}
          value={member.first_name}
          onChange={e => onUpdate('first_name', e.target.value)}
          error={errors[`member_${index}_first_name`]}
          placeholder='First Name'
        />
        {errors[`member_${index}_first_name`] && (
          <p className='mt-1 text-sm text-red-500'>
            {errors[`member_${index}_first_name`]}
          </p>
        )}
      </div>

      {/* Last Name */}
      <div>
        <TextInput
          type='text'
          id={`member_${index}_last_name`}
          value={member.last_name}
          onChange={e => onUpdate('last_name', e.target.value)}
          error={errors[`member_${index}_last_name`]}
          placeholder='Last Name'
        />
        {errors[`member_${index}_last_name`] && (
          <p className='mt-1 text-sm text-red-500'>
            {errors[`member_${index}_last_name`]}
          </p>
        )}
      </div>

      {/* Date of Birth */}
      <div>
        <TextInput
          type='text'
          inputMode='numeric'
          id={`member_${index}_date_of_birth`}
          value={member.date_of_birth}
          onChange={e =>
            onUpdate('date_of_birth', formatDateInput(e.target.value))
          }
          error={errors[`member_${index}_date_of_birth`]}
          placeholder='Date of Birth (mm/dd/yyyy)'
          maxLength={10}
        />
        {errors[`member_${index}_date_of_birth`] && (
          <p className='mt-1 text-sm text-red-500'>
            {errors[`member_${index}_date_of_birth`]}
          </p>
        )}
      </div>
    </div>
  );
}
